import React, { useState } from "react";
import { X, Sparkles, Loader2, ArrowRight, TrendingUp, TrendingDown, Minus, Activity } from "lucide-react";
import { toast } from "sonner";
import { fetchRecommendations } from "@/lib/forgeApi";

const BIAS_DOT = { bullish: "#00C805", bearish: "#FF3B30", neutral: "#FFD60A" };
const TREND_ICON = { bullish: TrendingUp, bearish: TrendingDown, neutral: Activity };

/**
 * "Recommend a strategy" modal.
 *
 * Reads the current ticker, asks the backend to score every template
 * against trend / IV / vol / earnings, and lists the top picks with
 * rationale bullets. Clicking a pick calls `onPick(rec)` so the parent
 * can load it straight into the builder.
 */
const RecommendModal = ({ onClose, onPick, defaultSymbol = "SPY" }) => {
  const [symbol, setSymbol] = useState(defaultSymbol);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const handleRun = async (sym) => {
    if (!sym) {
      toast.error("Pick a symbol");
      return;
    }
    setRunning(true);
    try {
      const r = await fetchRecommendations(sym.toUpperCase(), 6);
      if (r?.error) {
        toast.error(r.error);
        setResult(null);
      } else {
        setResult(r);
      }
    } catch {
      toast.error("Recommendation failed");
    } finally {
      setRunning(false);
    }
  };

  // Auto-run on open with the current symbol
  React.useEffect(() => {
    if (defaultSymbol) handleRun(defaultSymbol);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const profile = result?.profile;
  const TrendIcon = TREND_ICON[profile?.trend] || Activity;

  return (
    <div
      className="fixed inset-0 z-[55] bg-[#050505]/85 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="recommend-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-[#0a0a0c] border border-[#A98CFF]/30 rounded-2xl p-5 sm:p-6 w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#A98CFF] flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> Strategy recommender
            </div>
            <h3 className="text-lg font-medium tracking-tight mt-0.5">
              The right trade for right now
            </h3>
            <p className="text-[12px] text-white/45 mt-1 leading-relaxed">
              Based on current market data — trend, IV regime, realized vol, and
              earnings proximity. We rank every template by quant fit, not gut feel.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="recommend-close"
            className="p-1 text-white/55 hover:text-white border border-white/15 rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Symbol input */}
        <div className="flex items-end gap-2 mb-4">
          <label className="block flex-1">
            <span className="text-[10px] uppercase tracking-[0.18em] text-white/45">Symbol</span>
            <input
              type="text"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && handleRun(symbol)}
              data-testid="recommend-symbol-input"
              className="mt-1 w-full bg-[#050505] border border-white/10 focus:border-[#A98CFF]/60 outline-none rounded-md px-3 py-2 num text-white"
            />
          </label>
          <button
            type="button"
            onClick={() => handleRun(symbol)}
            disabled={running}
            data-testid="recommend-run"
            className="inline-flex items-center justify-center gap-1.5 bg-[#A98CFF] text-black hover:bg-[#C4B5FD] px-4 py-2 rounded-md text-[12.5px] font-medium disabled:opacity-50"
          >
            {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {running ? "Reading…" : "Recommend"}
          </button>
        </div>

        {/* Profile chip strip */}
        {profile && (
          <div
            className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2.5 mb-4 flex flex-wrap items-center gap-3"
            data-testid="recommend-profile"
          >
            <span
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em]"
              style={{ color: BIAS_DOT[profile.trend] }}
            >
              <TrendIcon className="w-3.5 h-3.5" /> {profile.trend}
            </span>
            <span className="text-white/30">·</span>
            <span className="text-[11px] uppercase tracking-[0.18em] text-white/65">
              IV{" "}
              <span className="text-[#A98CFF]">
                {profile.iv30_pct ? `${profile.iv30_pct.toFixed(1)}%` : "—"}
              </span>{" "}
              ({profile.iv_regime})
            </span>
            <span className="text-white/30">·</span>
            <span className="text-[11px] uppercase tracking-[0.18em] text-white/65">
              RV{" "}
              <span className="text-white">
                {profile.vol30_pct ? `${profile.vol30_pct.toFixed(1)}%` : "—"}
              </span>{" "}
              ({profile.vol_regime})
            </span>
            {profile.earnings_days != null && profile.earnings_days <= 30 && (
              <>
                <span className="text-white/30">·</span>
                <span
                  className="text-[11px] uppercase tracking-[0.18em]"
                  style={{ color: profile.earnings_days <= 7 ? "#FF7A33" : "#FFD60A" }}
                >
                  Earnings in {profile.earnings_days}d
                </span>
              </>
            )}
          </div>
        )}

        {/* Results list */}
        <div className="flex-1 overflow-auto pr-1 -mr-1">
          {!result && !running && (
            <div className="text-center text-white/40 text-[12px] py-12 italic">
              Set a symbol and hit Recommend.
            </div>
          )}
          {running && !result && (
            <div className="text-center text-white/55 py-12">
              <Loader2 className="w-5 h-5 animate-spin inline" />
            </div>
          )}
          {result?.recommendations?.length === 0 && (
            <div className="text-center text-white/40 text-[12px] py-8 italic">
              No clean fit for the current profile — try a different symbol.
            </div>
          )}
          {!!result?.recommendations?.length && (
            <div className="space-y-2">
              {result.recommendations.map((r, i) => (
                <button
                  key={r.template_id}
                  type="button"
                  onClick={() => onPick(r)}
                  data-testid={`recommend-pick-${i}`}
                  className="w-full text-left border border-white/10 hover:border-[#A98CFF]/40 rounded-lg p-3 transition group"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className="w-1.5 h-1.5 rounded-full"
                          style={{ background: BIAS_DOT[r.bias] || "#ffffff80" }}
                        />
                        <span className="text-[13px] font-medium text-white truncate">
                          {r.template_name}
                        </span>
                        <span className="text-[9px] uppercase tracking-[0.18em] text-white/40">
                          {r.category}
                        </span>
                      </div>
                      <ul className="space-y-0.5">
                        {r.rationale.map((rat, j) => (
                          <li
                            key={j}
                            className="text-[11.5px] text-white/60 leading-relaxed flex gap-1.5"
                          >
                            <span className="text-[#A98CFF]/60">·</span>
                            <span>{rat}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-white/40">
                        Score
                      </div>
                      <div className="num text-[18px] font-medium text-[#A98CFF]">
                        {r.score}
                      </div>
                      <span className="text-[10px] inline-flex items-center gap-0.5 text-[#A98CFF] group-hover:text-white">
                        Load <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecommendModal;
